import axios from 'axios';
import { ROUTES, RouteSegments } from 'constants/routes';
import { GuideType } from 'consts';
import { useAuthContext } from 'contexts/AuthContext';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';
import { IAppConfig, S4Feature, UnauthorizedReason } from 'types/api';
import { matchAppRouteOutsideRoute } from 'utils/routes';
import {
	isS4FeatureEnabled,
	normalizeMappingFeatures
} from 'utils/s4FeaturesUtils';
import { HTTP_STATUS_CODE } from './api';
import { API_ROUTES } from './apiRoutes';
import QueryKeys from './queryKeys';
import { staticBaseURL } from './setup';

const getAppConfig = async (
	projectId: string | null,
	tenantId: string | null
): Promise<IAppConfig> => {
	if (!projectId) {
		return {};
	}

	let projectConfig: IAppConfig = {};
	try {
		const res = await axios.get<IAppConfig>(
			`${staticBaseURL()}${API_ROUTES.appConfig(projectId)}`,
			{ allowAbsoluteUrls: true }
		);
		if (res.data) {
			projectConfig = res.data!;
		}
	} catch (err: any) {
		/* istanbul ignore if  */
		if (err.status !== HTTP_STATUS_CODE.notFound) {
			throw err;
		}
	}

	let tenantConfig: IAppConfig = {};

	try {
		if (tenantId) {
			const res = await axios.get<IAppConfig>(
				`${staticBaseURL()}${API_ROUTES.tenantAppConfig(projectId, tenantId)}`,
				{ allowAbsoluteUrls: true }
			);

			if (res.data) {
				tenantConfig = res.data;
			}
		}
	} catch (err: any) {
		/* istanbul ignore if  */
		if (err.status !== HTTP_STATUS_CODE.notFound) {
			throw err;
		}
	}

	const merged: IAppConfig = {
		...projectConfig,
		...tenantConfig
	};
	merged.features = normalizeMappingFeatures(merged.features);

	return merged;
};

export const useAppConfig = <T = IAppConfig>(
	select?: (data: IAppConfig) => T
) => {
	const { authRes, setAuthRes } = useAuthContext();

	return useQuery({
		queryKey: [QueryKeys.appConfig],
		enabled: !!authRes.projectId || authRes.done,
		queryFn: () => getAppConfig(authRes.projectId, authRes.tenantId),
		staleTime: Infinity,
		select,
		onError: () =>
			setAuthRes((prev) => ({
				...prev,
				unauthorizedReason: UnauthorizedReason.CONFIG_FETCH_FAILED
			}))
	});
};

export const useFeatureEnabled = (
	feature: S4Feature
): { enabled: boolean; configLoaded: boolean } => {
	const { data: enabled, isSuccess: configLoaded } = useAppConfig((appConfig) =>
		isS4FeatureEnabled(appConfig?.features, feature)
	);

	return { enabled: !!enabled, configLoaded };
};

export const useShouldAutoNavigateOnInit = (): boolean => {
	const location = useLocation();
	const pathnameMatch = matchAppRouteOutsideRoute(location.pathname);
	const { enabled: scimEnabled } = useFeatureEnabled(S4Feature.Scim);
	const { enabled: ssoEnabled } = useFeatureEnabled(S4Feature.Sso);

	// only jump straight into the SSO flow when it is the sole thing to set up.
	if (scimEnabled || !ssoEnabled) {
		return false;
	}

	return (
		pathnameMatch?.pattern.path === ROUTES.home ||
		pathnameMatch?.params[RouteSegments.Guide] === GuideType.SCIM
	);
};
